<template>
  <Teleport to="body">
    <div
      v-show="visible"
      class="layout-search-mask"
      @click.self="closeSearch"
      @mousedown.self="closeSearch"
   >
      <button
        type="button"
        class="layout-search-close"
        aria-label="关闭搜索"
        @click="closeSearch"
     >
        <el-icon><Close /></el-icon>
      </button>

      <div class="layout-search-center" @click.stop @mousedown.stop>
        <el-autocomplete
          ref="autocompleteRef"
          v-model="menuQuery"
          value-key="title"
          :fetch-suggestions="menuSearch"
          placeholder="菜单搜索：支持中文、路由路径"
          clearable
          trigger-on-focus
          fit-input-width
          :teleported="true"
          popper-class="layout-search-suggestions"
          class="layout-menu-search-autocomplete"
          @select="onSelect"
       >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
          <template #default="{ item }">
            <div class="search-item">
              <i v-if="item.icon" :class="iconClass(item.icon)" />
              <span>{{ item.title }}</span>
            </div>
          </template>
        </el-autocomplete>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { Close, Search } from '@element-plus/icons-vue';
import { useRouter } from 'vue-router';
import useCrmebMenuStore from '@/store/modules/menu';
import { crmebNodesToNavList, flattenCrmebNavMenus, ensureCrmebPrefix } from '@/utils/crmebMenu';
import { getMenuSearchList } from '@/api/system';

export interface MenuSearchItem {
  path: string;
  title: string;
  icon?: string;
  redirect?: string;
}

const router = useRouter();
const crmebMenu = useCrmebMenuStore();

const visible = ref(false);
const menuQuery = ref('');
const autocompleteRef = ref<{ focus?: () => void } | null>(null);
const localMenuList = ref<MenuSearchItem[]>([]);

function iconClass(icon: string) {
  return icon.startsWith('el-icon-') ? icon : `el-icon-${icon}`;
}

function normalizeMenuItem(raw: Record<string, unknown>): MenuSearchItem | null {
  const title = String(raw.title ?? raw.menuName ?? '').trim();
  const pathRaw = String(raw.path ?? '').trim();
  if (!title || !pathRaw) return null;
  return {
    title,
    path: ensureCrmebPrefix(pathRaw),
    icon: raw.icon ? String(raw.icon) : undefined,
    redirect: raw.redirect ? ensureCrmebPrefix(String(raw.redirect)) : undefined,
  };
}

function initLocalMenus() {
  const nav = crmebNodesToNavList(crmebMenu.menuTree);
  localMenuList.value = flattenCrmebNavMenus(nav);
}

function focusAutocomplete() {
  autocompleteRef.value?.focus?.();
}

function openSearch() {
  menuQuery.value = '';
  initLocalMenus();
  visible.value = true;
  nextTick(() => {
    setTimeout(focusAutocomplete, 120);
  });
}

function closeSearch() {
  visible.value = false;
  menuQuery.value = '';
}

function onEscapeKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && visible.value) {
    closeSearch();
  }
}

function menuSearch(queryString: string, cb: (items: MenuSearchItem[]) => void) {
  if (queryString) {
    getMenuSearchList({ is_mer: 1, keyword: queryString })
      .then((res: any) => {
        const menus = res?.data?.menus ?? res?.menus ?? [];
        const list = (Array.isArray(menus) ? menus : [])
          .map((m: Record<string, unknown>) => normalizeMenuItem(m))
          .filter(Boolean) as MenuSearchItem[];
        cb(list);
      })
      .catch(() => cb([]));
    return;
  }
  initLocalMenus();
  cb(localMenuList.value);
}

function onSelect(item: MenuSearchItem) {
  const target = item.redirect || item.path;
  closeSearch();
  if (target) {
    nextTick(() => router.push(target));
  }
}

watch(visible, (val) => {
  document.body.style.overflow = val ? 'hidden' : '';
});

onMounted(() => {
  document.addEventListener('keydown', onEscapeKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onEscapeKey);
  document.body.style.overflow = '';
});

defineExpose({ openSearch });
</script>

<style scoped lang="scss">
/* 全屏半透明遮罩（对齐 EB：浅色半透明 + 可点空白关闭） */
.layout-search-mask {
  position: fixed;
  inset: 0;
  z-index: 2800;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(2px);
  cursor: default;
}

.layout-search-close {
  position: fixed;
  top: 16px;
  right: 20px;
  z-index: 2802;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #606266;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  &:hover {
    color: var(--prev-color-primary, #4073fa);
  }
}

.layout-search-center {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  width: 560px;
  max-width: calc(100vw - 32px);
  z-index: 2801;
}

.layout-menu-search-autocomplete {
  width: 100%;
}

.layout-search-center :deep(.el-input__wrapper) {
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  min-height: 40px;
  border-radius: 4px;
}

.layout-search-center :deep(.el-input__inner) {
  height: 40px;
  line-height: 40px;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.5;
  padding: 4px 0;
}
</style>
